import axios from 'axios';
import { API_URL } from '../config/api.config';

// Use centralized API configuration
const API_BASE_URL = API_URL;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sales API
export const salesAPI = {
  getCategories: () => api.get('/sales/categories'),
  getProducts: (categoryId) => api.get(`/sales/products${categoryId ? `?category_id=${categoryId}` : ''}`),
  getSalespersons: () => api.get('/sales/salespersons'),
  createEstimate: (data) => api.post('/sales/estimates', data),
  getEstimates: (params) => api.get('/sales/estimates', { params }),
  getEstimateDetails: (id) => api.get(`/sales/estimates/${id}`),
  convertToSale: (id) => api.put(`/sales/estimates/${id}/sell`),
};

// Admin API
export const adminAPI = {
  getDashboard: (days = 30) => api.get(`/admin/dashboard?days=${days}`),
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  getProducts: (categoryId) => api.get(`/admin/products${categoryId ? `?category_id=${categoryId}` : ''}`),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getCustomers: () => api.get('/admin/customers'),
  getEmployees: () => api.get('/admin/employees'),
  getSalespersons: () => api.get('/admin/salespersons'),
  getSalesReport: (params) => api.get('/admin/reports/sales', { params }),
};

// Form data helper with fallback
const fetchFormData = async () => {
  const fallbackData = {
    categories: [
      { id: 1, name: 'Electronics' },
      { id: 2, name: 'Clothing' },
      { id: 3, name: 'Books' },
      { id: 4, name: 'Home & Garden' }
    ],
    products: [
      { id: 1, name: 'Sample Product 1', categoryId: 1, category_id: 1, sellingPrice: 100, quantity: 10 },
      { id: 2, name: 'Sample Product 2', categoryId: 2, category_id: 2, sellingPrice: 200, quantity: 5 },
      { id: 3, name: 'Sample Product 3', categoryId: 3, category_id: 3, sellingPrice: 50, quantity: 20 },
      { id: 4, name: 'Electronics Item A', categoryId: 1, category_id: 1, sellingPrice: 150, quantity: 8 },
      { id: 5, name: 'Clothing Item B', categoryId: 2, category_id: 2, sellingPrice: 75, quantity: 15 },
      { id: 6, name: 'Book Item C', categoryId: 3, category_id: 3, sellingPrice: 25, quantity: 30 }
    ],
    salespersons: [
      { id: 1, name: 'Default Sales Person' },
      { id: 2, name: 'Admin User' },
      { id: 3, name: 'Sales Manager' }
    ]
  };

  try {
    console.log('🔄 Attempting to fetch real data from server...');

    const [categoriesRes, productsRes, salespersonsRes] = await Promise.allSettled([
      adminAPI.getCategories(),
      adminAPI.getProducts(),
      adminAPI.getEmployees()
        .catch(() => adminAPI.getSalespersons())
        .catch(() => salesAPI.getSalespersons())
    ]);

    // Normalize product data to ensure consistent field names across all components
    const normalizeProducts = (products) => {
      if (!products || products.length === 0) {
        console.warn('⚠️ No products received from server, using fallback');
        return fallbackData.products;
      }

      return products.map(product => {
        const id = product.id || product._id || product.productID || '';
        const name = product.name || product.productName || product.product?.name || 'Unnamed Product';
        const catId = product.category_id || product.categoryId || product.categoryID || product.category?._id || product.product?.categoryId?._id || product.product?.categoryId || '';

        return {
          ...product,
          // Legacy/Common fields
          id: id,
          _id: id,
          name: name,
          productName: name,
          // Category mapping
          categoryId: catId,
          category_id: catId,
          // Price and stock
          sellingPrice: product.sellingPrice || product.selling_price || product.price || product.product?.sellingPrice || 0,
          quantity: product.quantity || product.stock || product.stockQuantity || product.product?.quantity || 0,
          stockQuantity: product.stockQuantity || product.quantity || product.stock || product.product?.quantity || 0,
          // Ensure product object exists for components that expect it
          product: product.product || {
            name: name,
            categoryId: catId,
            hsnCode: product.hsnCode || product.hsn_code || ''
          }
        };
      });
    };

    // Get real data or use fallback
    const categories = (categoriesRes.status === 'fulfilled' && categoriesRes.value.data?.length > 0)
      ? categoriesRes.value.data.map(cat => ({
        ...cat,
        id: cat.id || cat._id,
        _id: cat.id || cat._id,
        name: cat.name || cat.categoryName,
        categoryName: cat.categoryName || cat.name
      }))
      : fallbackData.categories;

    const rawProducts = (productsRes.status === 'fulfilled' && productsRes.value.data?.length > 0)
      ? productsRes.value.data
      : [];

    const products = normalizeProducts(rawProducts);

    const salespersons = (salespersonsRes.status === 'fulfilled' && salespersonsRes.value.data?.length > 0)
      ? salespersonsRes.value.data.map(sp => ({
        ...sp,
        id: sp.id || sp._id,
        _id: sp.id || sp._id,
        name: sp.name || sp.salesperson_name
      }))
      : fallbackData.salespersons;

    console.log('✅ Data loaded and normalized successfully:');
    console.log('  📊 Categories:', categories.length);
    console.log('  📦 Products:', products.length);
    console.log('  👥 Salespersons:', salespersons.length);

    return {
      categories,
      products,
      salespersons,
      fromServer: categoriesRes.status === 'fulfilled' && productsRes.status === 'fulfilled'
    };
  } catch (error) {
    console.error('❌ Error fetching data, using fallback:', error);
    return fallbackData;
  }
};

// Categories/products/salespersons rarely change while a user works, but this
// is called every time an edit or convert modal opens. Reuse the result for a
// minute and share in-flight requests so modals open without waiting.
const FORM_DATA_TTL_MS = 60 * 1000;
let formDataCache = null;
let formDataCachedAt = 0;
let formDataInFlight = null;

export const invalidateFormDataCache = () => {
  formDataCache = null;
  formDataCachedAt = 0;
};

const isFormDataMutation = (config) => {
  const method = (config?.method || 'get').toLowerCase();
  return method !== 'get' && /\/(products|categories)(\/|\?|$)/.test(config?.url || '');
};

export const watchFormDataMutations = (instance) => {
  instance.interceptors.response.use((response) => {
    if (isFormDataMutation(response.config)) invalidateFormDataCache();
    return response;
  });
};

watchFormDataMutations(api);

export const getFormData = async () => {
  if (formDataCache && Date.now() - formDataCachedAt < FORM_DATA_TTL_MS) {
    return formDataCache;
  }
  if (!formDataInFlight) {
    formDataInFlight = fetchFormData()
      .then((data) => {
        // Only cache real server data, not the hard-coded fallback
        if (data && data.fromServer) {
          formDataCache = data;
          formDataCachedAt = Date.now();
        }
        return data;
      })
      .finally(() => {
        formDataInFlight = null;
      });
  }
  return formDataInFlight;
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
};

// Initialize data
export const initializeData = () => api.post('/init');

// Guest/Public API
export const guestAPI = {
  getCompanySettings: () => api.get('/guest/company-settings'),
  getDocument: (shareToken) => api.get(`/guest/document/${shareToken}`),
  getProduct: (productToken) => api.get(`/guest/product/${productToken}`),
  getCategory: (categoryId) => api.get(`/guest/category/${categoryId}`),
  submitLead: (data) => api.post('/guest/lead', data),
};

export default api;
